import React, { useState, useEffect } from 'react';
import { Heart, MessageCircle, Send } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { getBackendUrl } from '../utils/getBackendUrl';

const API = `${getBackendUrl()}/api`;

const PhotoInteractions = ({ photo, user, onPhotoUpdate }) => {
  const [likes, setLikes] = useState([]);
  const [comments, setComments] = useState([]);
  const [isLiked, setIsLiked] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (photo?.id) {
      setIsLoading(true);
      fetchLikes();
      fetchComments();
    }
  }, [photo?.id]);

  const fetchLikes = async () => {
    try {
      const response = await axios.get(`${API}/photos/${photo.id}/likes`);
      const likesData = response.data?.likes || [];
      setLikes(likesData);
      setIsLiked(response.data?.user_liked || false);
    } catch (error) {
      console.error('Failed to fetch likes:', error);
      setLikes([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const response = await axios.get(`${API}/photos/${photo.id}/comments`);
      setComments(response.data || []);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
      setComments([]);
    }
  };

  const handleLike = async () => {
    try {
      const response = await axios.post(`${API}/photos/${photo.id}/like`);
      setIsLiked(response.data.liked);
      
      // Fetch updated likes list
      await fetchLikes();
      
      // Update parent component if callback provided
      if (onPhotoUpdate) {
        onPhotoUpdate(photo.id, { likes_count: response.data.likes_count });
      }
    } catch (error) {
      console.error('Like error:', error);
      toast.error('Failed to like photo');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/photos/${photo.id}/comment`, {
        comment: newComment.trim()
      });
      
      const currentComments = Array.isArray(comments) ? comments : [];
      setComments([...currentComments, response.data]);
      setNewComment('');
      toast.success('Comment added');
      
      // Update parent component
      if (onPhotoUpdate) {
        onPhotoUpdate(photo.id, { comments_count: currentComments.length + 1 });
      }
    } catch (error) {
      console.error('Comment error:', error);
      toast.error('Failed to add comment');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!photo?.id) {
    return null;
  }

  return (
    <div className="photo-interactions">
      {/* Like and Comment Buttons */}
      <div className="flex items-center gap-4 px-4 py-3 border-t border-gray-200 dark:border-gray-700">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleLike}
          disabled={isLoading}
          className={`flex items-center gap-2 ${isLiked ? 'text-red-500' : 'text-gray-600 dark:text-gray-400'}`}
        >
          <Heart 
            className={`w-5 h-5 ${isLiked ? 'fill-current' : ''}`}
          />
          <span className="text-sm font-medium">{Array.isArray(likes) ? likes.length : 0}</span>
        </Button>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowComments(!showComments)}
          disabled={isLoading}
          className="flex items-center gap-2 text-gray-600 dark:text-gray-400"
        >
          <MessageCircle className="w-5 h-5" />
          <span className="text-sm font-medium">{Array.isArray(comments) ? comments.length : 0}</span>
        </Button>
      </div>

      {/* Comments Section */}
      {showComments && (
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          {/* Existing Comments */}
          {Array.isArray(comments) && comments.length > 0 && (
            <div className="space-y-3 mb-4 max-h-60 overflow-y-auto">
              {comments.map((comment) => (
                <div key={comment.id} className="flex gap-2">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-medium">
                    {comment.user_name?.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <div className="bg-white dark:bg-gray-700 rounded-lg px-3 py-2">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {comment.user_name}
                      </p>
                      <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                        {comment.comment || comment.content}
                      </p>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-3">
                      {new Date(comment.created_at).toLocaleDateString('en-US', { timeZone: 'America/Toronto', month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add Comment Input */}
          <div className="flex gap-2">
            <Textarea
              placeholder="Add a comment..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleAddComment();
                }
              }}
              className="flex-1 min-h-[60px] text-sm"
              disabled={isSubmitting}
            />
            <Button
              onClick={handleAddComment}
              disabled={!newComment.trim() || isSubmitting}
              size="sm"
              className="self-end"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoInteractions;
